import { useState, useEffect, ReactNode } from 'react';
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js';
import { AuthContext, LastAuthEventContext } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

const DEMO_EMAIL = import.meta.env['VITE_DEMO_EMAIL'] as string | undefined;
// Optional closed-beta allowlist. When VITE_ALLOWED_TESTERS is unset or empty
// the app is OPEN to every signed-in user (launch default). Set it to a
// comma-separated list of emails to re-raise the "Early Access" wall for a
// private beta. Previously an unset var fell back to three hardcoded emails,
// which meant "remove the env var to go live" silently kept the wall up.
const ALLOWED_TESTERS = ((import.meta.env['VITE_ALLOWED_TESTERS'] as string | undefined) ?? '')
  .split(',')
  .map((e) => e.trim())
  .filter(Boolean);
const WALL_ENABLED = ALLOWED_TESTERS.length > 0;

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastAuthEvent, setLastAuthEvent] = useState<AuthChangeEvent | null>(null);
  const [isRestricted, setIsRestricted] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      if (cancelled) return;
      setLastAuthEvent(event);
      setSession(s);
      setUser(s?.user ?? null);
      const email = s?.user?.email ?? '';
      const demoEmail = DEMO_EMAIL ?? '';
      setIsRestricted(
        WALL_ENABLED && !!s && !ALLOWED_TESTERS.includes(email) && email !== demoEmail,
      );
      // On INITIAL_SESSION with no session while a PKCE code is pending,
      // keep loading=true — supabase-js's auto-detect will fire SIGNED_IN
      // momentarily after exchanging the code. Setting isLoading=false now
      // would prematurely render the unauthenticated route tree.
      const hasPendingCode = window.location.search.includes('code=');
      if (!hasPendingCode || s) {
        setIsLoading(false);
      }
      // Clean PKCE ?code= from URL when auth resolves
      if (s && hasPendingCode) {
        window.history.replaceState({}, '', window.location.pathname);
      }
    });

    // Safety net: if supabase-js auto-detect fails to fire SIGNED_IN
    // within 5 seconds despite a pending code, stop loading so the
    // OAuthCallback component can show its error UI.
    const hasCode = window.location.search.includes('code=');
    let fallback: ReturnType<typeof setTimeout> | undefined;
    if (hasCode) {
      fallback = setTimeout(() => setIsLoading(false), 5000);
    }

    return () => {
      cancelled = true;
      subscription.unsubscribe();
      if (fallback) clearTimeout(fallback);
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signup = async (email: string, password: string, metadata?: Record<string, string>) => {
    const { error, data } = await supabase.auth.signUp({
      email,
      password,
      options: { data: metadata },
    });
    if (error) throw error;
    return { requiresConfirmation: !data.session };
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const authValue = {
    user: user
      ? {
          id: user.id,
          email: user.email ?? '',
          firstName: user.user_metadata?.['first_name'] ?? '',
          lastName: user.user_metadata?.['last_name'] ?? '',
          businessName: user.user_metadata?.['business_name'] ?? '',
        }
      : null,
    isAuthenticated: !!user,
    isRestricted,
    isLoading,
    session,
    login,
    signup,
    logout,
  };

  return (
    <LastAuthEventContext.Provider value={lastAuthEvent}>
      <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
    </LastAuthEventContext.Provider>
  );
}
